import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextInput, Button, Stack, Text, Anchor } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconMailCheck } from '@tabler/icons-react';
import { vendorsApi } from '../../../api/vendors';
import { StatusIcon, VendorAuthLayout } from './VendorAuthLayout';
import { BRAND_MEDIUM } from '../../../theme/brand';

const BACK_TO_LOGIN = { label: 'Back to sign in', to: '/vendor/login' };

export function VendorForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (!value ? 'Email is required' : !/^\S+@\S+$/.test(value) ? 'Invalid email' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);

    try {
      await vendorsApi.requestPasswordReset(values.email);
      setEmailSent(true);
      notifications.show({
        title: 'Password Reset Email Sent',
        message: 'Please check your inbox for password reset instructions.',
        color: 'green',
      });
    } catch (err: any) {
      // Don't reveal if email exists for security
      setEmailSent(true);
      notifications.show({
        title: 'Email Sent',
        message: 'If an account exists with this email, a password reset link has been sent.',
        color: 'green',
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <VendorAuthLayout
        back={BACK_TO_LOGIN}
        icon={
          <StatusIcon>
            <IconMailCheck size={30} aria-hidden />
          </StatusIcon>
        }
        title="Check your email"
        subtitle={`If an account exists for ${form.values.email}, we've sent a link to reset your password.`}
      >
        <Text size="sm" c="dimmed">
          Open the link in the email to choose a new password. It expires in 1 hour. Check your spam folder if you
          can't find it.
        </Text>

        <Button component={Link} to="/vendor/login" color={BRAND_MEDIUM} fullWidth size="md" radius="md">
          Back to sign in
        </Button>

        <Text size="sm" ta="center" c="dimmed">
          Didn't receive the email?{' '}
          <Anchor component="button" type="button" onClick={() => setEmailSent(false)} fw={600} c={BRAND_MEDIUM}>
            Try again
          </Anchor>
        </Text>
      </VendorAuthLayout>
    );
  }

  return (
    <VendorAuthLayout
      back={BACK_TO_LOGIN}
      title="Reset your password"
      subtitle="Enter the email you registered with and we'll send you a reset link."
    >
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="you@company.com"
            type="email"
            autoComplete="email"
            required
            size="md"
            radius="md"
            {...form.getInputProps('email')}
          />

          <Button type="submit" loading={loading} fullWidth color={BRAND_MEDIUM} size="md" radius="md">
            Send reset link
          </Button>
        </Stack>
      </form>

      <Text size="sm" ta="center" c="dimmed">
        Remember your password?{' '}
        <Anchor component={Link} to="/vendor/login" fw={600} c={BRAND_MEDIUM}>
          Sign in
        </Anchor>
      </Text>
    </VendorAuthLayout>
  );
}
